import { z } from 'zod';
// ============= Detection Pattern Types (Issue #381) =============
/**
 * Categories of detection patterns.
 */
export const DetectionPatternCategory = z.enum([
    'api', // API endpoints (Express, Fastify, NestJS, etc.)
    'database', // Database queries (Prisma, SQL, Mongoose, TypeORM)
    'httpClient', // HTTP clients (Fetch, Axios, Got)
    'queue', // Message queues (BullMQ, RabbitMQ, SQS, Kafka)
    'storage', // File/object storage (S3, GCS, filesystem)
    'auth', // Authentication patterns (JWT, OAuth, session)
    'error', // Error handling patterns
]);
/**
 * A single detection pattern definition.
 */
export const DetectionPatternSchema = z.object({
    /** Unique identifier for the pattern (e.g., 'express-route', 'prisma-query') */
    key: z.string(),
    /** Human-readable name */
    name: z.string(),
    /** Description of what this pattern detects */
    description: z.string(),
    /** Category this pattern belongs to */
    category: DetectionPatternCategory,
    /** The regex pattern string */
    regex: z.string(),
    /** Regex flags (e.g., 'gi' for global case-insensitive) */
    flags: z.string().default('gi'),
    /** Default confidence score (0-1) */
    confidence: z.number().min(0).max(1).default(0.8),
    /** Framework or technology this pattern is associated with */
    framework: z.string().optional(),
    /** Example code that would match this pattern */
    example: z.string().optional(),
    /** Whether this is a built-in default pattern */
    isDefault: z.boolean().default(true),
});
/**
 * User override for a detection pattern.
 */
export const DetectionPatternOverrideSchema = z.object({
    /** Database ID */
    id: z.string(),
    /** Project this override belongs to */
    projectId: z.string(),
    /** Category of the pattern */
    category: DetectionPatternCategory,
    /** Key of the pattern being overridden */
    patternKey: z.string(),
    /** Whether the pattern is enabled */
    enabled: z.boolean().default(true),
    /** Custom regex pattern (null = use default) */
    customRegex: z.string().nullable().optional(),
    /** Custom confidence score */
    confidence: z.number().min(0).max(1).nullable().optional(),
    /** User description of the custom pattern */
    description: z.string().nullable().optional(),
    /** Creation timestamp */
    createdAt: z.date().or(z.string()),
    /** Update timestamp */
    updatedAt: z.date().or(z.string()),
});
/**
 * A pattern with its effective state (default + override merged).
 */
export const EffectivePatternSchema = z.object({
    /** The default pattern definition */
    pattern: DetectionPatternSchema,
    /** The user's override (if any) */
    override: DetectionPatternOverrideSchema.nullable().optional(),
    /** Whether the pattern is currently enabled */
    enabled: z.boolean(),
    /** The effective regex to use */
    effectiveRegex: z.string(),
    /** The effective confidence score */
    effectiveConfidence: z.number(),
    /** Whether this pattern has been customized */
    isCustomized: z.boolean(),
});
/**
 * Input for creating/updating a pattern override.
 */
export const UpdatePatternOverrideSchema = z.object({
    /** Whether the pattern is enabled */
    enabled: z.boolean().optional(),
    /** Custom regex pattern (null to use default) */
    customRegex: z.string().nullable().optional(),
    /** Custom confidence score (null to use default) */
    confidence: z.number().min(0).max(1).nullable().optional(),
    /** User description */
    description: z.string().nullable().optional(),
});
/**
 * Input for testing a regex pattern.
 */
export const TestPatternInputSchema = z.object({
    /** The regex pattern to test */
    regex: z.string(),
    /** Regex flags */
    flags: z.string().default('gi'),
    /** Sample code to test against */
    sampleCode: z.string(),
});
/**
 * Result of testing a regex pattern.
 */
export const TestPatternResultSchema = z.object({
    /** Whether the regex is valid */
    valid: z.boolean(),
    /** Error message if regex is invalid */
    error: z.string().optional(),
    /** Number of matches found */
    matchCount: z.number(),
    /** The matches found */
    matches: z.array(z.object({
        /** The matched text */
        text: z.string(),
        /** Line number (1-indexed) */
        line: z.number(),
        /** Character index in the full text */
        index: z.number(),
    })),
});
/**
 * Response for listing patterns with overrides.
 */
export const PatternListResponseSchema = z.object({
    /** Category being listed */
    category: DetectionPatternCategory,
    /** All patterns in this category with their effective state */
    patterns: z.array(EffectivePatternSchema),
});
/**
 * Response for listing all patterns grouped by category.
 */
export const AllPatternsResponseSchema = z.object({
    /** All patterns grouped by category */
    categories: z.record(DetectionPatternCategory, z.array(EffectivePatternSchema)),
    /** Total number of patterns */
    totalPatterns: z.number(),
    /** Number of customized patterns */
    customizedCount: z.number(),
});
// ============= Helper Functions =============
/**
 * Get display name for a pattern category.
 */
export function getCategoryDisplayName(category) {
    const names = {
        api: 'API Endpoints',
        database: 'Database Queries',
        httpClient: 'HTTP Clients',
        queue: 'Message Queues',
        storage: 'File Storage',
        auth: 'Authentication',
        error: 'Error Handling',
    };
    return names[category] || category;
}
/**
 * Get description for a pattern category.
 */
export function getCategoryDescription(category) {
    const descriptions = {
        api: 'Patterns for detecting API routes and endpoints (Express, Fastify, NestJS, etc.)',
        database: 'Patterns for detecting database operations (Prisma, SQL, Mongoose, TypeORM)',
        httpClient: 'Patterns for detecting outbound HTTP requests (Fetch, Axios, Got)',
        queue: 'Patterns for detecting message queue operations (BullMQ, RabbitMQ, SQS, Kafka)',
        storage: 'Patterns for detecting file/object storage operations (S3, GCS, filesystem)',
        auth: 'Patterns for detecting authentication mechanisms (JWT, OAuth, session)',
        error: 'Patterns for detecting error handling middleware and handlers',
    };
    return descriptions[category] || '';
}
/**
 * Validate a regex pattern string.
 */
export function validateRegex(pattern, flags = 'gi') {
    try {
        new RegExp(pattern, flags);
        return { valid: true };
    }
    catch (e) {
        return { valid: false, error: e instanceof Error ? e.message : 'Invalid regex' };
    }
}
//# sourceMappingURL=detection.js.map